import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useState } from 'react';
import { connect } from 'react-redux';

import { track } from '@/actions/tracking';
import { notify, popup } from '@/actions/ui';
import Button from '@/components/Button';
import CvButton from '@/components/CvButton';
import Divider from '@/components/Divider';
import SocialButton from '@/components/SocialButton';
import Tidbit from '@/components/Tidbit';
import { openPictureDialog, openResumeDialog } from '@/core/attachment';
import { useApp } from '@/hooks/useAppContext';
import { useFileUploadErrorHandler } from '@/hooks/useFileUploadErrorHandler';
import {
  CV_ATTACH_ERROR,
  CV_REMOVE_ERROR,
  PICTURE_SAVE_ERROR,
  reportProfileSaveFailure,
} from '@/profile/profileSaveFailure';
import { updatePicture, updateProfile } from '@/profile/store';
import { fetchUser } from '@/store/user';
import { darkGreen } from '@/theme/colors';

import Badges from './Badges';
import Dialogs from './Dialogs';
import Education from './Education';
import Experience from './Experience';
import Expertise from './Expertise';
import Headline from './Headline';
import ProfileCompleteness from './ProfileCompleteness';
import Questions from './Questions';
import Sidebar from './Sidebar';
import Summary from './Summary';

const getStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 20,
    alignItems: 'flex-start',
  },
  defaultCover: {
    backgroundColor: darkGreen,
    height: 120,
    width: '100%',
  },
  tidbit: {
    // @ts-expect-error TS(2339): Property 'breakpoints' does not exist on type 'Def... Remove this comment to see the full error message
    [theme.breakpoints.down('md')]: {
      margin: '0 auto',
      textAlign: 'center',
    },
  },
  cvButton: {
    margin: '25px 0',
  },
  footer: {
    marginTop: 30,
    textAlign: 'right',
    // @ts-expect-error TS(2339): Property 'breakpoints' does not exist on type 'Def... Remove this comment to see the full error message
    [theme.breakpoints.down('md')]: {
      textAlign: 'center',
    },
  },
  paddingMobile: {
    // @ts-expect-error TS(2339): Property 'breakpoints' does not exist on type 'Def... Remove this comment to see the full error message
    [theme.breakpoints.down('md')]: {
      padding: '0 10px',
    },
  },
}));

function ProfilePreview({
  profileId,
  updateProfile,
  updatePicture,
  notify,
  editable = true,
  viewer,
  user,
  profile,
  className,
  handleSubmit,
  ...other
}: any) {
  const s = getStyles();

  const { graphql } = useApp();
  const onUploadError = useFileUploadErrorHandler();
  const theme = useTheme();

  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  const [editSections, setEditSections] = useState({});
  const [completenessClosed, setCompletenessClosed] = useState(false);

  const handleUpdatePicture = useCallback(
    (file: any) => {
      updatePicture(profileId, file.url).catch((err: unknown) =>
        reportProfileSaveFailure(err, notify, PICTURE_SAVE_ERROR)
      );
    },
    [profileId, updatePicture, notify]
  );

  const getSectionsInitialState = useCallback(() => {
    const sections = { ...editSections };
    Object.keys(sections).forEach((k) => {
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      sections[k] = false;
    });
    return sections;
  }, [editSections]);

  const openDialog = useCallback(
    (key: any) => {
      setEditSections({
        ...getSectionsInitialState(),
        [key]: true,
      });
    },
    [setEditSections, getSectionsInitialState]
  );

  const closeDialog = useCallback(() => {
    setEditSections(getSectionsInitialState());
  }, [setEditSections, getSectionsInitialState]);

  const attachCV = useCallback(
    (file: any) => {
      if (!file) return;
      updateProfile({ id: profileId, cv_url: file.url }).catch((err: unknown) =>
        reportProfileSaveFailure(err, notify, CV_ATTACH_ERROR)
      );
    },
    [profileId, updateProfile, notify]
  );

  const removeCV = useCallback(() => {
    updateProfile({ id: profileId, cv_url: '' }).catch((err: unknown) =>
      reportProfileSaveFailure(err, notify, CV_REMOVE_ERROR)
    );
  }, [profileId, updateProfile, notify]);

  const handlePictureDialog = () => {
    openPictureDialog(graphql.client)
      .then((files) => files.length > 0 && handleUpdatePicture(files[0]))
      .catch((error) => onUploadError(error, 'photo'));
  };

  const handleResumeDialog = () => {
    openResumeDialog(graphql.client)
      .then((files) => attachCV(files[0]))
      .catch((error) => onUploadError(error, 'CV'));
  };

  if (!profile) return null;

  return (
    <form className={className} onSubmit={(e) => handleSubmit(e)}>
      <Grid container spacing={mobile ? 0 : 4}>
        {mobile && <div className={s.defaultCover} />}

        <Grid item md={3} sm={12} xs={12} className={s.paddingMobile}>
          <Sidebar
            preview
            user={user}
            profile={profile}
            pictureEditable
            onPictureChange={handleUpdatePicture}
            onEditName={() => openDialog('name')}
            requestUrl={profile.request_url}
          />
        </Grid>

        <Grid item md={9} sm={12} xs={12} className={s.paddingMobile}>
          {editable && !mobile && !completenessClosed && (
            <ProfileCompleteness
              profileId={profileId}
              isSignup
              onRequestClose={() => setCompletenessClosed(true)}
              style={{ marginBottom: 20 }}
              nameAction={() => openDialog('name')}
              pictureAction={handlePictureDialog}
              introductionAction={() => openDialog('headline')}
              experiencesAction={() => openDialog('experiences')}
              keywordsAction={() => openDialog('keywords')}
              expertiseAction={() => openDialog('expertise')}
              questionsAction={() => openDialog('questions')}
              locationAction={() => openDialog('location')}
              communicationAction={() => openDialog('communication')}
              phoneAction={() => openDialog('phones')}
              educationsAction={() => openDialog('education')}
              linkedinAction={() => openDialog('linkedin')}
              resumeAction={handleResumeDialog}
            />
          )}

          {!mobile && (
            <Badges
              viewer={viewer}
              user={user}
              profile={profile}
              onEditMarketplace={() => openDialog('available_marketplace')}
            />
          )}

          {!mobile && <Divider />}

          <div className={s.row}>
            <Tidbit
              className={s.tidbit}
              city={profile.city}
              country={profile.country}
              languages={profile.languages}
              editable={editable}
              inline={!mobile}
              onEditLocation={() => openDialog('location')}
              onEditLanguages={() => openDialog('communication')}
            />

            {profile.linkedin_url && !mobile && (
              <SocialButton startIcon="linkedin" href={profile.linkedin_url} />
            )}
          </div>

          <Headline editable profile={profile} onEdit={() => openDialog('headline')} />

          {mobile && <Divider />}

          <Summary editable profile={profile} onEdit={() => openDialog('headline')} />

          <div className={s.cvButton}>
            <CvButton
              editable={editable}
              url={profile.cv_url}
              onUpload={attachCV}
              onRemove={removeCV}
            />
          </div>

          <Divider />

          <Questions profile={profile} editable={editable} onEdit={() => openDialog('questions')} />

          <Divider />

          <Expertise profile={profile} editable={editable} onEdit={() => openDialog('expertise')} />

          <Divider />

          <Experience
            experiences={profile.experiences}
            editable={editable}
            onEdit={() => openDialog('experiences')}
          />

          <Divider />

          <Education
            education={profile.education}
            editable={editable}
            onEdit={() => openDialog('education')}
          />

          <Divider />

          <Dialogs
            user={user}
            profile={profile}
            editSections={editSections}
            closeDialog={closeDialog}
          />

          {other.form && (
            <div className={s.footer}>
              <Button size="large" type="submit" fullWidth={mobile}>
                Finish
              </Button>
            </div>
          )}
        </Grid>
      </Grid>
    </form>
  );
}

export default connect(
  (state, ownProps) => ({
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    viewer: state.viewer,
    // @ts-expect-error TS(2339): Property 'userId' does not exist on type '{}'.
    user: ownProps.userId ? state.users[ownProps.userId] : undefined,
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    profile: state.profiles.fullProfiles[ownProps.profileId],
  }),
  {
    fetchUser,
    updateProfile,
    updatePicture,
    popup,
    track,
    notify,
  }
)(ProfilePreview);
